import { BOSS_MOOD, type BossMoodConfig } from '../../../config/atmosphere/bossMood.config';

/**
 * BossMoodModel — the pure boss-phase swell accumulator behind BossMoodDriver.
 * No I/O, no events, no Pixi — just phase tracking per boss npcId → a
 * scene-mood multiplier, so the mapping is fully unit-testable. The driver
 * owns the event wiring + emit.
 *
 * Tracks how many phase transitions each boss has undergone, maps the count
 * to an escalating intensity from {@link BossMoodConfig.phaseIntensities},
 * and takes the MAX across all active bosses (the most intense fight
 * dominates the mood). On defeat, the boss is cleared; when no bosses
 * remain, the mood returns to neutral (1).
 */
export class BossMoodModel {
  private bossPhases = new Map<string, number>();
  private mood = 1;

  constructor(private readonly cfg: BossMoodConfig = BOSS_MOOD) {}

  /** Register a boss phase advance. Returns the new global boss mood. */
  onPhaseChange(npcId: string): number {
    const count = (this.bossPhases.get(npcId) ?? 0) + 1;
    this.bossPhases.set(npcId, count);
    this.recompute();
    return this.mood;
  }

  /** Clear a boss on defeat. Returns the new global boss mood. */
  onDefeat(npcId: string): number {
    this.bossPhases.delete(npcId);
    this.recompute();
    return this.mood;
  }

  /** Current boss-mood multiplier (1 = neutral, >1 = swelled). */
  value(): number {
    return this.mood;
  }

  /** Number of tracked active bosses (test/debug). */
  get activeBossCount(): number {
    return this.bossPhases.size;
  }

  /** Phase count for a specific boss (test/debug). */
  phaseCount(npcId: string): number {
    return this.bossPhases.get(npcId) ?? 0;
  }

  /** Reset all state (e.g. on level unload). */
  reset(): void {
    this.bossPhases.clear();
    this.mood = this.cfg.neutral;
  }

  private recompute(): void {
    let max = this.cfg.neutral;
    for (const [, count] of this.bossPhases) {
      const idx = Math.min(count - 1, this.cfg.phaseIntensities.length - 1);
      const intensity = this.cfg.phaseIntensities[idx] ?? this.cfg.neutral;
      if (intensity > max) max = intensity;
    }
    this.mood = max;
  }
}
